import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  BookOpen, Users, BarChart3, Shield, Globe, CreditCard,
  ChevronRight, Check, Star, GraduationCap, ClipboardList,
  ChevronDown, ChevronUp, Building2, Smartphone, ArrowRight,
  Zap, Lock, Phone,
} from 'lucide-react';

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/3223a00e-a889-4935-a373-cc79c7b40f4f/images/b6b64aaa7049246add76103dda69c59edddde079c9ab90dc4c1c35979bc16fc7.png';

export default function LandingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [visible, setVisible] = useState({});
  const observerRef = useRef(null);

  useEffect(() => {
    publicApi.getPlans().then(r => {
      if (Array.isArray(r.data)) setPlans(r.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) setVisible(v => ({ ...v, [e.target.id]: true }));
      });
    }, { threshold: 0.15 });
    document.querySelectorAll('[data-animate]').forEach(el => observerRef.current.observe(el));
    return () => observerRef.current?.disconnect();
  }, [plans]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const features = [
    { icon: BookOpen, title: 'Suivi Coranique Complet', desc: 'Evaluations quotidiennes Bind, Wett et Nafarr. Suivez la progression de chaque eleve sourate par sourate.', accent: 'from-emerald-600 to-emerald-700' },
    { icon: Users, title: 'Gestion des Membres', desc: 'Inscrivez, organisez et suivez tous vos eleves et enseignants avec des fiches detaillees.', accent: 'from-emerald-600 to-emerald-700' },
    { icon: CreditCard, title: 'Frais & Paiements', desc: 'Gerez les inscriptions, les frais mensuels et l\'internat. Historique complet des paiements.', accent: 'from-amber-500 to-amber-600' },
    { icon: ClipboardList, title: 'Evaluations & Bulletins', desc: 'Evaluations mensuelles, trimestrielles et export PDF. Bulletins automatises.', accent: 'from-emerald-600 to-emerald-700' },
    { icon: Shield, title: 'Securite & Roles', desc: 'Controle d\'acces par roles. Chaque ecole dispose d\'un espace totalement isole et prive.', accent: 'from-stone-700 to-stone-800' },
    { icon: BarChart3, title: 'Rapports & Statistiques', desc: 'Tableaux de bord en temps reel. Visualisez la performance de votre ecole d\'un coup d\'oeil.', accent: 'from-amber-500 to-amber-600' },
  ];

  const steps = [
    { num: '01', title: 'Inscrivez votre ecole', desc: 'Formulaire rapide en 2 etapes. Choisissez votre identifiant unique.', icon: Building2 },
    { num: '02', title: 'Configurez votre espace', desc: 'Ajoutez enseignants, creez classes et inscrivez vos eleves.', icon: Users },
    { num: '03', title: 'Gerez tout en ligne', desc: 'Evaluations, paiements, progression : tout dans un seul tableau de bord.', icon: Zap },
  ];

  const testimonials = [
    { name: 'Ousmane Ba', role: 'Directeur, Daara Al-Azhar', text: 'Daaraj a transforme la gestion de notre ecole. Le suivi coranique est devenu tellement plus simple pour nos enseignants.', stars: 5 },
    { name: 'Fatou Diop', role: 'Secretaire, Ecole Islamique Touba', text: 'La plateforme est intuitive et complete. Nos parents apprecient la transparence sur les paiements et les evaluations.', stars: 5 },
    { name: 'Ibrahima Sall', role: 'Enseignant, Daara Serigne Touba', text: 'En tant qu\'enseignant, je peux evaluer mes eleves directement depuis mon telephone. Un gain de temps enorme.', stars: 5 },
  ];

  const faqs = [
    { q: 'Qu\'est-ce que Daaraj ?', a: 'Daaraj est une plateforme SaaS de gestion d\'ecoles coraniques et classiques. Chaque ecole dispose de son propre espace securise avec toutes les fonctionnalites necessaires.' },
    { q: 'Puis-je essayer gratuitement ?', a: 'Oui ! Le plan Gratuit vous permet de gerer jusqu\'a 50 eleves sans aucun engagement. Vous pouvez evoluer vers un plan superieur a tout moment.' },
    { q: 'Comment sont securisees les donnees ?', a: 'Chaque ecole dispose de sa propre base de donnees isolee. L\'acces est protege par des roles et des permissions.' },
    { q: 'Quels moyens de paiement sont acceptes ?', a: 'Nous acceptons les paiements par carte bancaire (Stripe) et par mobile money (Orange Money, Wave) via PayDunya.' },
    { q: 'L\'application fonctionne-t-elle sur mobile ?', a: 'Oui ! Les enseignants disposent d\'une application mobile dediee (PWA). L\'interface d\'administration est egalement responsive.' },
  ];

  const formatPrice = (p) => new Intl.NumberFormat('fr-FR').format(p);
  const anim = (id) => visible[id] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8';

  return (
    <div className="min-h-screen bg-[#fafaf8] overflow-x-hidden">
      <style>{`
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        .float-anim { animation: float 6s ease-in-out infinite; }
        .glass { backdrop-filter: blur(16px); background: rgba(255,255,255,0.7); }
      `}</style>

      {/* ============ NAV ============ */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center shadow-lg shadow-emerald-200/50">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-black tracking-tight text-stone-900">daaraj</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            {['features', 'pricing', 'faq'].map(s => (
              <button key={s} onClick={() => scrollTo(s)} className="text-[13px] font-medium text-stone-400 hover:text-stone-900 transition-colors uppercase tracking-wider">
                {s === 'features' ? 'Fonctionnalites' : s === 'pricing' ? 'Tarifs' : 'FAQ'}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} className="text-stone-500 font-medium" data-testid="nav-login-btn">
              Connexion
            </Button>
            <Button size="sm" onClick={() => navigate('/register')} className="bg-stone-900 hover:bg-stone-800 text-white rounded-lg px-4 shadow-lg" data-testid="nav-register-btn">
              Demarrer
              <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* ============ HERO ============ */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 px-6 overflow-hidden">
        {/* BG decorations */}
        <div className="absolute top-20 -left-32 w-96 h-96 bg-emerald-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute top-40 -right-32 w-80 h-80 bg-amber-100 rounded-full blur-[100px] opacity-40" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent" />

        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16 relative">
          <div className="flex-1 max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-100 rounded-full mb-6">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Plateforme SaaS</span>
            </div>

            <h1 className="text-[2.75rem] sm:text-5xl lg:text-[3.5rem] font-black text-stone-900 leading-[1.08] tracking-tight">
              Gerez votre ecole.
              <br />
              <span className="bg-gradient-to-r from-emerald-600 to-emerald-400 bg-clip-text text-transparent">
                Simplement.
              </span>
            </h1>

            <p className="mt-6 text-base lg:text-lg text-stone-400 leading-relaxed max-w-md">
              Daaraj digitalise la gestion de votre Daara. Eleves, evaluations coraniques, enseignants, paiements — tout centralise dans un espace securise.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-start gap-3">
              <Button size="lg" onClick={() => navigate('/register')} className="bg-stone-900 hover:bg-stone-800 text-white rounded-xl px-7 h-13 text-[15px] shadow-xl shadow-stone-300/30" data-testid="hero-register-btn">
                Commencer gratuitement
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="ghost" size="lg" onClick={() => scrollTo('features')} className="text-stone-500 h-13 text-[15px]" data-testid="hero-features-btn">
                En savoir plus
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {/* Trust badges */}
            <div className="mt-12 flex items-center gap-6 text-xs text-stone-400">
              <div className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Donnees securisees</div>
              <div className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Gratuit jusqu'a 50 eleves</div>
            </div>
          </div>

          {/* Hero visual */}
          <div className="flex-1 flex justify-center relative">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-b from-emerald-200/30 to-transparent rounded-3xl blur-2xl scale-110" />
              <img
                src={HERO_IMG}
                alt="Daaraj - Gestion d'ecole"
                className="relative w-full max-w-lg float-anim drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============ LOGOS / STATS ============ */}
      <section id="stats-section" data-animate className={`py-16 px-6 transition-all duration-700 ${anim('stats-section')}`}>
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '100+', label: 'Ecoles', icon: Building2 },
              { value: '5 000+', label: 'Eleves geres', icon: GraduationCap },
              { value: '99.9%', label: 'Disponibilite', icon: Zap },
              { value: '3', label: 'Langues', icon: Globe },
            ].map((s, i) => (
              <div key={i} className="text-center p-6 rounded-2xl bg-white border border-stone-100 shadow-sm" data-testid={`stat-${i}`}>
                <s.icon className="w-5 h-5 text-stone-300 mx-auto mb-3" />
                <p className="text-2xl font-black text-stone-900">{s.value}</p>
                <p className="text-xs text-stone-400 mt-1 font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section className="py-24 px-6" id="features">
        <div className="max-w-6xl mx-auto">
          <div id="feat-title" data-animate className={`mb-16 max-w-lg transition-all duration-700 ${anim('feat-title')}`}>
            <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-3">Fonctionnalites</p>
            <h2 className="text-3xl sm:text-4xl font-black text-stone-900 leading-tight">
              Tout pour votre Daara.<br />Rien de superflu.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <div
                key={i}
                id={`feat-${i}`}
                data-animate
                className={`group relative p-6 rounded-2xl bg-white border border-stone-100 hover:border-stone-200 shadow-sm hover:shadow-xl transition-all duration-500 ${anim(`feat-${i}`)}`}
                style={{ transitionDelay: `${i * 80}ms` }}
                data-testid={`feature-card-${i}`}
              >
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${f.accent} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                  <f.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-stone-900 mb-2">{f.title}</h3>
                <p className="text-sm text-stone-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="py-24 px-6 bg-stone-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-5">
          <div className="absolute top-10 left-10 w-64 h-64 border border-white/20 rounded-full" />
          <div className="absolute bottom-10 right-20 w-96 h-96 border border-white/10 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-48 h-48 border border-white/15 rounded-full" />
        </div>

        <div className="max-w-5xl mx-auto relative">
          <div id="steps-title" data-animate className={`text-center mb-16 transition-all duration-700 ${anim('steps-title')}`}>
            <p className="text-xs font-bold text-emerald-400 uppercase tracking-widest mb-3">Comment ca marche</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white">Trois etapes. C'est tout.</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div
                key={i}
                id={`step-${i}`}
                data-animate
                className={`relative p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm transition-all duration-700 ${anim(`step-${i}`)}`}
                style={{ transitionDelay: `${i * 150}ms` }}
                data-testid={`step-${i}`}
              >
                <span className="text-5xl font-black text-white/5 absolute top-4 right-6">{s.num}</span>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5">
                  <s.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-stone-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SHOWCASE ============ */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div id="show-1" data-animate className={`flex flex-col lg:flex-row items-center gap-16 mb-24 transition-all duration-700 ${anim('show-1')}`}>
            <div className="flex-1">
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-3">Suivi Coranique</p>
              <h3 className="text-2xl sm:text-3xl font-black text-stone-900 mb-4 leading-tight">114 sourates. Un suivi precis.</h3>
              <p className="text-stone-400 leading-relaxed mb-8">
                Evaluations quotidiennes Bind, Wett et Nafarr avec selection de sourates. L'historique complet de chaque eleve est accessible en un clic.
              </p>
              <div className="space-y-3">
                {['Progression automatique sourate par sourate', 'Evaluations en un tap depuis le mobile', 'Historique et statistiques par eleve'].map((t, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </div>
                    <span className="text-sm text-stone-600">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <div className="relative p-8 bg-gradient-to-br from-emerald-50 to-emerald-50/50 rounded-3xl border border-emerald-100">
                <div className="grid grid-cols-3 gap-3">
                  {['Al-Fatiha', 'Al-Baqara', 'Al-Imran', 'An-Nisa', 'Al-Ma\'ida', 'Al-An\'am', 'Al-A\'raf', 'Al-Anfal', 'At-Tawba'].map((surah, i) => (
                    <div key={i} className={`p-3 rounded-xl text-center text-xs font-medium transition-all ${
                      i < 4 ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' :
                      i < 6 ? 'bg-emerald-200 text-emerald-800' :
                      'bg-white text-stone-400 border border-stone-100'
                    }`}>
                      {surah}
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <span className="text-emerald-600 font-bold">4/114 completees</span>
                  <div className="w-24 h-1.5 bg-emerald-100 rounded-full overflow-hidden">
                    <div className="w-[3.5%] h-full bg-emerald-500 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="show-2" data-animate className={`flex flex-col lg:flex-row-reverse items-center gap-16 transition-all duration-700 ${anim('show-2')}`}>
            <div className="flex-1">
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-3">Multi-Ecoles</p>
              <h3 className="text-2xl sm:text-3xl font-black text-stone-900 mb-4 leading-tight">Un espace prive par ecole.</h3>
              <p className="text-stone-400 leading-relaxed mb-8">
                Chaque ecole dispose de sa propre base de donnees isolee. Personnalisez les couleurs, le logo et les parametres selon vos besoins.
              </p>
              <div className="space-y-3">
                {['Base de donnees isolee par ecole', 'Personnalisation couleurs et logo', 'App mobile dediee pour les enseignants'].map((t, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-amber-600" />
                    </div>
                    <span className="text-sm text-stone-600">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-1">
              <div className="relative p-6 bg-gradient-to-br from-amber-50 to-amber-50/50 rounded-3xl border border-amber-100">
                <div className="space-y-3">
                  {[
                    { name: 'CARFEIS Dakar', color: '#16a34a', students: 245, status: 'active' },
                    { name: 'Daara Al-Azhar', color: '#16a34a', students: 128, status: 'active' },
                    { name: 'Ecole Islamique Touba', color: '#d97706', students: 89, status: 'pending' },
                  ].map((school, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-stone-100 shadow-sm">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: school.color }}>
                        <Building2 className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-stone-900 truncate">{school.name}</p>
                        <p className="text-xs text-stone-400">{school.students} eleves</p>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${school.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                        {school.status === 'active' ? 'Active' : 'En attente'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section className="py-24 px-6 bg-white" id="pricing">
        <div className="max-w-5xl mx-auto">
          <div id="price-title" data-animate className={`text-center mb-16 transition-all duration-700 ${anim('price-title')}`}>
            <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-3">Tarifs</p>
            <h2 className="text-3xl sm:text-4xl font-black text-stone-900">Simple. Transparent. Juste.</h2>
            <p className="text-stone-400 mt-3">Commencez gratuitement. Evoluez quand vous etes pret.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {plans.map((plan, i) => {
              const isPremium = plan.id === 'premium';
              const isEnterprise = plan.id === 'enterprise';
              return (
                <div
                  key={plan.id}
                  id={`plan-${i}`}
                  data-animate
                  className={`relative rounded-2xl transition-all duration-700 ${anim(`plan-${i}`)}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {isPremium && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                      <span className="px-4 py-1 bg-stone-900 text-white text-[10px] font-bold uppercase tracking-wider rounded-full shadow-lg">
                        Recommande
                      </span>
                    </div>
                  )}
                  <div className={`h-full p-7 rounded-2xl border-2 ${
                    isPremium ? 'border-stone-900 bg-stone-900 text-white shadow-2xl shadow-stone-300/30' :
                    'border-stone-100 bg-white'
                  }`}>
                    <h3 className={`text-sm font-bold uppercase tracking-wider ${isPremium ? 'text-stone-400' : 'text-stone-400'}`}>
                      {plan.name}
                    </h3>
                    <div className="mt-4 mb-6">
                      {plan.price_monthly === 0 ? (
                        <span className={`text-4xl font-black ${isPremium ? 'text-white' : 'text-stone-900'}`}>Gratuit</span>
                      ) : (
                        <>
                          <span className={`text-4xl font-black ${isPremium ? 'text-white' : 'text-stone-900'}`}>{formatPrice(plan.price_monthly)}</span>
                          <span className={`text-sm ml-1 ${isPremium ? 'text-stone-400' : 'text-stone-400'}`}>FCFA/mois</span>
                        </>
                      )}
                    </div>
                    <ul className="space-y-3 mb-8">
                      {(plan.features || []).map((f, j) => (
                        <li key={j} className="flex items-start gap-2.5 text-sm">
                          <Check className={`w-4 h-4 mt-0.5 shrink-0 ${isPremium ? 'text-emerald-400' : 'text-emerald-500'}`} />
                          <span className={isPremium ? 'text-stone-300' : 'text-stone-500'}>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={`w-full h-11 rounded-xl font-semibold ${
                        isPremium ? 'bg-white text-stone-900 hover:bg-stone-100' :
                        'bg-stone-900 text-white hover:bg-stone-800'
                      }`}
                      onClick={() => navigate('/register')}
                    >
                      {plan.price_monthly === 0 ? 'Commencer' : isEnterprise ? 'Contactez-nous' : 'Essai gratuit'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <div id="testi-title" data-animate className={`text-center mb-16 transition-all duration-700 ${anim('testi-title')}`}>
            <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-3">Temoignages</p>
            <h2 className="text-3xl sm:text-4xl font-black text-stone-900">Ils nous font confiance</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div
                key={i}
                id={`testi-${i}`}
                data-animate
                className={`p-6 rounded-2xl bg-white border border-stone-100 shadow-sm transition-all duration-700 ${anim(`testi-${i}`)}`}
                style={{ transitionDelay: `${i * 100}ms` }}
                data-testid={`testimonial-${i}`}
              >
                <div className="flex gap-0.5 mb-4">
                  {[...Array(t.stars)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-sm text-stone-500 leading-relaxed mb-6">"{t.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-stone-50">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-black text-emerald-700">
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-stone-900">{t.name}</p>
                    <p className="text-[11px] text-stone-400">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-24 px-6 bg-white" id="faq">
        <div className="max-w-2xl mx-auto">
          <div id="faq-title" data-animate className={`text-center mb-12 transition-all duration-700 ${anim('faq-title')}`}>
            <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-3">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-black text-stone-900">Questions frequentes</h2>
          </div>

          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`rounded-xl border transition-all ${openFaq === i ? 'border-stone-200 bg-stone-50 shadow-sm' : 'border-stone-100 bg-white'}`}
                data-testid={`faq-${i}`}
              >
                <button
                  className="w-full flex items-center justify-between p-5 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-sm font-bold text-stone-900 pr-4">{faq.q}</span>
                  <div className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 transition-all ${openFaq === i ? 'bg-stone-900 border-stone-900 rotate-180' : 'border-stone-200'}`}>
                    <ChevronDown className={`w-3.5 h-3.5 ${openFaq === i ? 'text-white' : 'text-stone-400'}`} />
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-40 pb-5' : 'max-h-0'}`}>
                  <p className="px-5 text-sm text-stone-400 leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-stone-900" />
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-600/10 rounded-full blur-[100px]" />
          <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-amber-500/10 rounded-full blur-[80px]" />
        </div>

        <div className="max-w-2xl mx-auto text-center relative">
          <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight">
            Pret a moderniser<br />votre Daara ?
          </h2>
          <p className="mt-4 text-stone-400 leading-relaxed">
            Rejoignez les ecoles qui font confiance a Daaraj. Inscription gratuite, sans engagement.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-white text-stone-900 hover:bg-stone-100 rounded-xl px-8 h-13 text-[15px] font-bold shadow-xl"
              data-testid="cta-register-btn"
            >
              Commencer gratuitement
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="ghost"
              size="lg"
              onClick={() => navigate('/login')}
              className="text-stone-400 hover:text-white h-13 text-[15px]"
            >
              J'ai deja un compte
            </Button>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-12 px-6 bg-stone-950">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center">
                  <BookOpen className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="font-black text-white">daaraj</span>
              </div>
              <p className="text-sm text-stone-500 leading-relaxed max-w-xs">
                Plateforme SaaS de gestion d'ecoles coraniques et classiques. Concue et developpee au Senegal.
              </p>
            </div>
            <div>
              <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-4">Produit</h4>
              <div className="space-y-2.5">
                {[
                  { label: 'Fonctionnalites', action: () => scrollTo('features') },
                  { label: 'Tarifs', action: () => scrollTo('pricing') },
                  { label: 'FAQ', action: () => scrollTo('faq') },
                ].map((l, i) => (
                  <button key={i} onClick={l.action} className="block text-sm text-stone-500 hover:text-white transition-colors">{l.label}</button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-4">Contact</h4>
              <div className="space-y-2.5">
                <button onClick={() => navigate('/login')} className="block text-sm text-stone-500 hover:text-white transition-colors">Connexion</button>
                <button onClick={() => navigate('/register')} className="block text-sm text-stone-500 hover:text-white transition-colors">S'inscrire</button>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-stone-800/50 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[11px] text-stone-600">2026 Daaraj SaaS. Tous droits reserves.</p>
            <button
              onClick={() => navigate('/super-admin/login')}
              className="text-[11px] text-stone-800 hover:text-stone-500 transition-colors"
              data-testid="footer-superadmin-link"
            >
              Administration
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
